import { useState } from 'react';
import { getExternalProviderAuthSessionId, setSessionState } from '../../utils/auth';
import { handleRequest } from '../../utils/request';
import { log } from '../../utils/logger';
import { isPasswordValid, PasswordValidation } from '../../Component/PasswordValidation';
import TermsAndConditions from '../../Component/TermsAndConditions';

import type { ReactElement } from 'react';

interface ISignUpModalContentProps {
	onSuccessCallback: () => void;
}

const SignUpModalContent = (props: ISignUpModalContentProps): ReactElement => {
	const [loading, setLoading] = useState(false);
	const [password, setPassword] = useState(``);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(``);
	const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

	const sessionId = getExternalProviderAuthSessionId();

	const canSignUp = (): boolean => {
		return hasAcceptedTerms && isPasswordValid(password);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
		event.preventDefault();
		if (!sessionId) {
			setErrorMessage(`Unable to retrieve session information`);
			return;
		}
		setLoading(true);
		const domain = window.localStorage.getItem(`domain`);
		const [error, reqResult] = await handleRequest({
			route: `signUp`,
			data: { sessionId, password },
			query: { domain: domain === `us` ? `US` : `UK` },
		});
		setLoading(false);

		if (error || !reqResult) {
			setErrorMessage(error?.message ?? `Something went wrong`);
			return;
		}

		const { data } = reqResult;
		const [setSessionStateErr] = await setSessionState(data.user.token, `v1`);
		if (setSessionStateErr) {
			log.unhappy(`Session state set error`, { error: setSessionStateErr.message });
			setErrorMessage(setSessionStateErr.message);
			return;
		}
		props.onSuccessCallback();
	};

	return (
		<div
			className={`
			    flex items-center justify-center
			    min-h-full 
			    px-4 sm:px-6 lg:px-8
			    max-w-[350px]
		    `}
		>
			<div className="max-w-md w-full space-y-8">
				{errorMessage && (
					<div
						className={`
						flex items-center
						px-3 py-2
						w-full
						bg-red-100
						rounded
						text-sm
					`}
					>
						There was an error. {errorMessage}
					</div>
				)}
				<form className="space-y-6" onSubmit={handleSubmit}>
					<span className="font-medium">
						Set your password in order to complete the onboarding and be able to use the app
					</span>
					<input type="hidden" name="remember" value="true" />
					<div className="rounded-md shadow-sm -space-y-px">
						<div className={`mt-2`}>
							<label className="sr-only text-sm">Password</label>
							<input
								id="password"
								name="password"
								type="password"
								autoComplete="current-password"
								required
								className={`
									block
									relative
									w-full
									px-3 py-2
									border border-gray-300 rounded-b-md focus:border-gray-500
									placeholder-gray-500
									text-gray-900 sm:text-sm 
									focus:outline-none
									focus:ring-gray-500
									focus:z-10
									appearance-none
								`}
								placeholder="Enter your password"
								value={password}
								onChange={(event): void => setPassword(event.target.value)}
							/>
						</div>
					</div>
					<PasswordValidation password={password} />
					<TermsAndConditions
						hasAccepetedTerms={hasAcceptedTerms}
						changeHasAcceptedTerms={(): void => setHasAcceptedTerms(!hasAcceptedTerms)}
					/>
					<div>
						<button
							type="submit"
							className={`
								group
								flex justify-center
								relative
								w-full
								mt-2
								py-3 px-4
								border border-transparent rounded-md
								text-white text-sm
								font-medium
								${canSignUp() ? `bg-primary hover:bg-primary-dark` : `bg-gray-300`}
								focus:outline-none
								focus:ring-2 focus:ring-offset-2 focus:ring-gray-500
							`}
							disabled={!canSignUp() || loading}
						>
							{loading ? `Please wait...` : `Sign Up`}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default SignUpModalContent;
